import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';
import {
  getBilling,
  subscribe,
  confirmSubscription,
  cancelSubscription,
  type Billing,
} from '../api/billing';
import { getApiClient } from '../api/factory';
import { redirectTo } from '../lib/redirect';
import { useAuthStore } from '../store/auth';
import { useT, type DictKey, type TFn } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { SkeletonList } from '../components/Skeleton';
import { InvoiceSection } from '../components/InvoiceSection';

// Сетка апгрейда — зеркало docs/pricing.md / billing.plans.go (источник
// истины). Бэкенд /me/billing отдаёт лимиты/цену только ТЕКУЩЕГО плана; для
// карточек выбора держим статичную сетку здесь (отдельного эндпойнта плана
// нет — бэк готов). Зона РФ (₽); Enterprise — sales-led (счёт/договор, без
// self-serve карты — см. pricing.md).
interface UpgradePlan {
  plan: 'pro' | 'business' | 'enterprise';
  priceRubMinor: number;
  seats: number; // 0 = безлимит
  salesLed?: boolean;
}

const UPGRADE_PLANS: readonly UpgradePlan[] = [
  { plan: 'pro', priceRubMinor: 169000, seats: 3 },
  { plan: 'business', priceRubMinor: 449000, seats: 10 },
  { plan: 'enterprise', priceRubMinor: 2499000, seats: 0, salesLed: true },
] as const;

const PLAN_NAME_KEY: Record<string, DictKey> = {
  free: 'billing.plan.free',
  pro: 'billing.plan.pro',
  business: 'billing.plan.business',
  enterprise: 'billing.plan.enterprise',
};

const STATUS_KEY: Record<string, DictKey> = {
  none: 'billing.status.none',
  pending: 'billing.status.pending',
  active: 'billing.status.active',
  canceled: 'billing.status.canceled',
  past_due: 'billing.status.pastDue',
};

const ENTERPRISE_CONTACT = 'mailto:sales@anexica.com?subject=Enterprise';

function formatRub(minor: number): string {
  return new Intl.NumberFormat('ru-RU').format(Math.round(minor / 100)) + ' ₽';
}

function planName(t: TFn, plan: string): string {
  return t(PLAN_NAME_KEY[plan] ?? 'billing.plan.free');
}

// Страница «Тариф и оплата» (ANX-060, слой 4). Admin-only: подписка/отмена —
// admin-эндпойнты; не-админам показываем заглушку. returnStatus прокидывает
// router из ?status (возврат с оплаты банка).
export function BillingPage({ returnStatus }: { returnStatus?: string }) {
  const t = useT();
  const qc = useQueryClient();
  const operator = useAuthStore((s) => s.session?.operator);
  const isAdmin = operator?.role === 'admin';

  const billingQuery = useQuery({
    queryKey: ['billing'],
    queryFn: () => getBilling(getApiClient()),
    enabled: isAdmin,
  });

  const [actionError, setActionError] = useState<string | null>(null);

  const subscribeMu = useMutation({
    mutationFn: (plan: string) => subscribe(getApiClient(), plan),
    onSuccess: (res) => {
      setActionError(null);
      redirectTo(res.paymentUrl);
    },
    onError: () => setActionError(t('billing.subscribeError')),
  });

  const cancelMu = useMutation({
    mutationFn: () => cancelSubscription(getApiClient()),
    onSuccess: () => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: () => setActionError(t('billing.cancelError')),
  });

  // Возврат с оплаты: ?status=paid → подтверждаем подписку у банка (активируем
  // план), затем обновляем read-модель. Срабатывает один раз на маунте.
  const confirmMu = useMutation({
    mutationFn: () => confirmSubscription(getApiClient()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing'] }),
  });
  const confirmedRef = useRef(false);
  useEffect(() => {
    if (!isAdmin || confirmedRef.current) return;
    if (returnStatus === 'paid') {
      confirmedRef.current = true;
      confirmMu.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, returnStatus]);

  if (!isAdmin) {
    return (
      <PageContainer width="narrow">
        <PageHeader title={t('billing.title')} />
        <p className="text-sm text-slate-500">{t('billing.adminOnly')}</p>
      </PageContainer>
    );
  }

  const b = billingQuery.data;

  return (
    <PageContainer width="wide">
      <PageHeader title={t('billing.title')} description={t('billing.subtitle')} />

      {returnStatus === 'paid' && (
        <Banner tone="success">
          {confirmMu.data?.status === 'active'
            ? t('billing.returnPaid')
            : t('billing.returnPaidPending')}
        </Banner>
      )}
      {returnStatus === 'failed' && <Banner tone="error">{t('billing.returnFailed')}</Banner>}
      {actionError && <Banner tone="error">{actionError}</Banner>}

      {billingQuery.isLoading ? (
        <SkeletonList rows={4} ariaLabel={t('common.loading')} />
      ) : billingQuery.isError || !b ? (
        <Banner tone="error">{t('billing.loadError')}</Banner>
      ) : (
        <div className="space-y-6">
          <CurrentPlan
            b={b}
            onCancel={() => cancelMu.mutate()}
            canceling={cancelMu.isPending}
          />
          <UsageSection b={b} />
          <UpgradeGrid
            currentPlan={b.plan}
            onSubscribe={(plan) => subscribeMu.mutate(plan)}
            subscribing={subscribeMu.isPending}
          />
          <InvoiceSection />
        </div>
      )}
    </PageContainer>
  );
}

function CurrentPlan({
  b,
  onCancel,
  canceling,
}: {
  b: Billing;
  onCancel: () => void;
  canceling: boolean;
}) {
  const t = useT();
  const periodEnd = b.currentPeriodEnd
    ? new Date(b.currentPeriodEnd).toLocaleDateString()
    : null;
  const isPaidActive = b.plan !== 'free' && b.status === 'active';

  return (
    <section className="rounded-md border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">
            {t('billing.currentPlan')}
          </div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">
            {planName(t, b.plan)}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {t(STATUS_KEY[b.status] ?? 'billing.status.none')}
            {periodEnd && ` · ${t('billing.periodEnd')} ${periodEnd}`}
          </div>
        </div>
        {isPaidActive && (
          <button
            type="button"
            onClick={onCancel}
            disabled={canceling}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {canceling ? t('billing.canceling') : t('billing.cancelSubscription')}
          </button>
        )}
      </div>
    </section>
  );
}

function UsageSection({ b }: { b: Billing }) {
  const t = useT();
  const dialogsLimit = b.limits.dialogsPerMonth; // 0 = безлимит
  const overDialogs =
    dialogsLimit > 0 && b.usage.dialogsThisMonth >= dialogsLimit;
  const nearDialogs =
    dialogsLimit > 0 &&
    !overDialogs &&
    b.usage.dialogsThisMonth >= Math.floor(dialogsLimit * 0.8);

  return (
    <section className="rounded-md border border-slate-200 bg-white p-5 shadow-card">
      <h2 className="mb-4 text-sm font-medium text-slate-900">
        {t('billing.usage.heading')}
      </h2>
      <div className="grid gap-5 sm:grid-cols-2">
        <UsageBar
          label={t('billing.usage.seats')}
          used={b.usage.seatsUsed}
          limit={b.limits.seats}
        />
        <UsageBar
          label={t('billing.usage.dialogs')}
          used={b.usage.dialogsThisMonth}
          limit={dialogsLimit}
        />
      </div>

      {overDialogs && (
        <Banner tone="error" className="mt-4">
          {t('billing.paywall.over', { limit: String(dialogsLimit) })}
        </Banner>
      )}
      {nearDialogs && (
        <Banner tone="warn" className="mt-4">
          {t('billing.paywall.warn', {
            used: String(b.usage.dialogsThisMonth),
            limit: String(dialogsLimit),
          })}
        </Banner>
      )}
    </section>
  );
}

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const t = useT();
  const unlimited = limit === 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const tone = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-accent-600';
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span className="text-slate-700">{label}</span>
        <span className="text-slate-500">
          {used} / {unlimited ? t('billing.usage.unlimited') : limit}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        {!unlimited && (
          <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} aria-hidden="true" />
        )}
      </div>
    </div>
  );
}

function UpgradeGrid({
  currentPlan,
  onSubscribe,
  subscribing,
}: {
  currentPlan: string;
  onSubscribe: (plan: string) => void;
  subscribing: boolean;
}) {
  const t = useT();
  return (
    <section>
      <h2 className="mb-4 text-sm font-medium text-slate-900">{t('billing.upgradeHeading')}</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        {UPGRADE_PLANS.map((p) => {
          const name = planName(t, p.plan);
          const isCurrent = currentPlan === p.plan;
          return (
            <div
              key={p.plan}
              className={
                'flex flex-col rounded-md border bg-white p-5 shadow-card ' +
                (isCurrent ? 'border-accent-400 ring-1 ring-accent-200' : 'border-slate-200')
              }
            >
              <div className="text-lg font-semibold text-slate-900">{name}</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">
                {formatRub(p.priceRubMinor)}
                <span className="text-sm font-normal text-slate-500"> {t('billing.perMonth')}</span>
              </div>
              <div className="mt-2 text-sm text-slate-500">
                {p.seats === 0
                  ? t('billing.seatsUnlimited')
                  : t('billing.seats', { n: String(p.seats) })}
              </div>

              <div className="mt-4 flex-1" />

              {isCurrent ? (
                <span className="inline-flex items-center justify-center rounded-md bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500">
                  {t('billing.currentPlanBadge')}
                </span>
              ) : p.salesLed ? (
                <a
                  href={ENTERPRISE_CONTACT}
                  aria-label={t('billing.enterpriseAria')}
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {t('billing.enterpriseContact')}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => onSubscribe(p.plan)}
                  disabled={subscribing}
                  aria-label={t('billing.subscribeAria', { plan: name })}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                >
                  <CreditCard size={15} aria-hidden="true" />
                  {subscribing ? t('billing.subscribing') : t('billing.subscribeCard')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Banner({
  tone,
  className,
  children,
}: {
  tone: 'success' | 'warn' | 'error';
  className?: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-red-200 bg-red-50 text-red-800';
  return (
    <div
      role="alert"
      className={`mb-4 rounded-md border px-4 py-2.5 text-sm ${toneClass} ${className ?? ''}`}
    >
      {children}
    </div>
  );
}
